/**
 * AutoCAD system variable schema for Dynamic Input, drawing aids and
 * related session-level prefs. Default values match a fresh-install
 * AutoCAD 2025+ session (verified via AutoCAD MCP on 2026-04 — see
 * Phase 0 plan Context for raw MCP readout).
 *
 * Important: values here are the AutoCAD parity defaults. They are the
 * first source consulted by the sysvarStore on initial construction and
 * the fallback whenever localStorage / DXF HEADER hydration fails.
 *
 * Do NOT change numeric defaults to satisfy tests — fix the tests. The
 * numbers are the product of AutoCAD parity, not developer preference.
 */

export const DYN_DEFAULTS = {
  // Dynamic Input master switch (bitmask: 1 = pointer input, 2 = dim input).
  DYNMODE: 3,
  // 0 = polar tooltip (distance + angle), 1 = cartesian (X, Y).
  DYNPICOORDS: 0,
  // 0 = relative coordinates (@), 1 = absolute (#).
  DYNPIFORMAT: 0,
  // Pointer tooltip visibility. 0 = command-only, 1 = on keystroke, 2 = always.
  DYNPIVIS: 1,
  // Dimension input visibility. 0 = none, 1 = one field, 2 = all fields.
  DYNDIVIS: 1,
  // Grip-edit field bitmask. 1 = Resize, 2 = Length, 4 = Angle, 8 = Arc radius, 16 = Move.
  DYNDIGRIP: 31,
  // Dynamic prompts shown near cursor (0 / 1).
  DYNPROMPT: 1,
  // Drafting tooltips (0 / 1) — controls colour/appearance surface.
  DYNTOOLTIPS: 1,
  // Temporary override keys (Shift etc.) enabled (0 / 1).
  TEMPOVERRIDES: 1,
  // Legacy drawing aids migrated into the shared sysvar store.
  ORTHOMODE: 0,
  POLARMODE: 0,
  // OTRACKMODE — object-snap tracking (OTRACK / F11) master on/off switch.
  // AutoCAD parity NOTE: real AutoCAD does not expose a documented top-level
  // GETVAR for this (it lives inside AUTOSNAP, whose bit layout this app
  // already committed to a DIFFERENT documented meaning for bits 8/16/32 —
  // see the AUTOSNAP comment below). This app models OTRACK's master switch
  // as its OWN dedicated sysvar instead, exactly mirroring how ORTHOMODE and
  // POLARMODE (both ALSO folded-out-of-AUTOSNAP concepts in real AutoCAD)
  // already get their own top-level sysvar here — same pragmatic deviation
  // `POLARMODE_TRACKING_ON_BIT`'s own doc explains for POLAR. AutoCAD
  // default = 0 (off).
  OTRACKMODE: 0,
  // POLARANG — polar tracking angle increment in DEGREES (AutoCAD default 90,
  // so bare polar tracking behaves like ORTHO). Unlike every other member of
  // this schema POLARANG is a REAL (the AutoCAD menu offers 90/45/30/22.5/18/
  // 15/10/5, and SETVAR accepts any positive angle ≤ 360) — its validator
  // branch below is exempted from the global integer gate. Registry-persisted
  // in AutoCAD (not a DXF HEADER var), so it round-trips localStorage only.
  POLARANG: 90,
  // POLARDIST — polar-snap distance increment (real, AutoCAD default 0 = use
  // the SNAP X-spacing). Registry-persisted in AutoCAD (not a DXF HEADER var),
  // like POLARANG. AutoCAD real docs: "Sets the snap increment for polar
  // snap. If ... 0, PolarSnap ... assumes the value for Snap X spacing."
  // FOLLOW-UP (N6 POLAR-RELATIVE-BASIS-WIRE): this app has no SNAPTYPE/
  // PolarSnap grid-increment engine yet (SNAPMODE itself has no cursor-
  // snapping behaviour wired — see `SnapGridTab.tsx`), so POLARDIST is
  // schema-recognised (SETVAR/GETVAR round-trip; no DSettings widget) but
  // does not yet constrain the cursor. Documented gap, not a silent
  // omission (CLAUDE.md "Допустимо только: документировать известные
  // незавершённые подсистемы").
  POLARDIST: 0,
  // TRACKPATH — display of polar / object-snap tracking alignment paths
  // (bitcode, AutoCAD default 0). Bit 2 ("does not display polar tracking
  // path") is wired: `resolveDraftAidConstraint` suppresses ONLY the dashed
  // path LINE when set (`polar.pathVisible`) — the LOCKED CURSOR POSITION
  // and the "distance < angle°" AutoTrack TOOLTIP are BOTH unaffected
  // (TRACKPATH is documented as gating the alignment path only, never the
  // tooltip or the snap behaviour — Autodesk TRACKPATH reference). Bit 1
  // ("full-screen vs point-limited") and the OSNAP-tracking-path bits are
  // NOT modelled: this app's polar guide is always point-limited (anchor to
  // cursor) and there is no OSNAP tracking-path overlay yet (a separate,
  // not-yet-built OTRACK subsystem) for the other two bits to gate.
  TRACKPATH: 0,
  SNAPMODE: 0,
  GRIDMODE: 0,
  // Object snap mode bitmask (4133 = ENDPOINT | MIDPOINT | CENTER | INTERSECTION).
  OSMODE: 4133,
  // MIRRTEXT — MIRROR text-handling switch. 0 = text stays READABLE when
  // mirrored (insertion point reflects, glyphs are not flipped); 1 = text
  // mirrors fully like ordinary geometry (backwards glyphs). AutoCAD 2025
  // fresh-install default = 0. Round-trips through the DXF HEADER ($MIRRTEXT,
  // group 70, int16) via the drawing-model `mirrtext` field.
  MIRRTEXT: 0,
  // OSNAP marker / aperture / magnet behaviour bitmask. AutoCAD default = 63
  //   bit  1 = marker (yellow glyph at snap point)
  //   bit  2 = SnapTip (label tooltip at snap point)
  //   bit  4 = magnet (cursor jumps to the snap point — REAL positional snap)
  //   bit  8 = display the aperture box at the cursor
  //   bit 16 = ignore hatch objects when snapping
  //   bit 32 = replace Z value of snapped point with current elevation
  AUTOSNAP: 63,
  // OSNAP aperture half-size in CSS pixels. AutoCAD default = 10.
  APERTURE: 10,
  // PICKBOX selection-target half-size in CSS pixels. AutoCAD default = 3
  // (visible box is ~6 px wide). Reference: Autodesk knowledge-base
  // PICKBOX (2025). Used to draw the AutoCAD-style pickbox sprite at the
  // cursor in idle (no draft command) selection mode.
  PICKBOX: 3,
  // CURSORSIZE — crosshair length as percentage of the smaller viewport
  // dimension. 1..100; default 5 means ±2.5% from cursor centre. 100 =
  // full-screen crosshair (alignment mode where the lines extend off-
  // canvas in every direction). Reference: Autodesk knowledge-base
  // CURSORSIZE (2025).
  CURSORSIZE: 5,
  // CURSORCOLOR — crosshair colour index. 0..256 (AutoCAD ACI palette;
  // 7 is the contrast colour that flips white-on-dark / black-on-light
  // automatically). Default 7 matches AutoCAD's "Crosshair Color" field
  // in the Display tab of OPTIONS.
  CURSORCOLOR: 7,
  // Lineweight display in screen space (0 / 1).
  LWDISPLAY: 0,
  // Right-mouse-button acts as Enter (0 = shortcut menu, 1 = Enter).
  RMBENTER: 0,
  // SELECTIONPREVIEW (B17 hover-highlight). Bit 1 = preview when no
  // command active, bit 2 = preview during a selection prompt. Default
  // 3 = both. 0 = disabled (matches AutoCAD).
  //
  // Note: the renderer reads from `previewPreferences` (zustand slice
  // backed by localStorage); the sysvar slot here is kept in lock-step
  // by the bridge in `previewSysvarBridge.ts` so SETVAR PREVIEW... and
  // the Options dialog stay in sync.
  SELECTIONPREVIEW: 3,
  // PREVIEWEFFECT — emphasis style for SELECTIONPREVIEW. 0 = dashed
  // outline (AutoCAD default), 1 = glowing outline + face fill (HW
  // gate, deferred), 2 = both. The renderer falls back to outline-only
  // for value 1 and emits a one-time console warning at the call-site.
  PREVIEWEFFECT: 0,
  // PREVIEWFILTER — six-bit mask gating which entity categories are
  // eligible for hover preview. Default 7 = locked layer | xrefs |
  // tables (the three AutoCAD always excludes). Higher bits 8/16/32
  // cover mlines / hatches / groups (user-extensible).
  PREVIEWFILTER: 7,
  // ROLLOVERTIPS — toggles the hover property tooltip card. 0 = off,
  // 1 = on (AutoCAD default).
  ROLLOVERTIPS: 1,
  // SELECTIONCYCLING (B12) — controls behavior when stacked / overlapping
  // entities sit under the cursor. Per Autodesk SELECTIONCYCLING docs:
  //   0 = off (single-entity pick wins, no badge, no listbox)
  //   1 = badge only — small ▦ icon next to cursor when ≥2 entities
  //       are within the pickbox (single click still picks topmost)
  //   2 = badge + listbox dialog — badge shows on hover, click opens a
  //       popup with all stacked candidates so user picks explicitly
  // Default 2 (AutoCAD 2025 fresh-install). Ctrl+W toggles between
  // 2 (badge+dialog) and 0 (off); Shift+Space silently cycles regardless.
  SELECTIONCYCLING: 2,
  // UNDOLIMIT — maximum entries in the undo history past[] stack. 0
  // disables undo entirely (history never accumulates). AutoCAD 2025
  // default is 100; valid range 0..10 000. When UNDOLIMIT decreases,
  // oldest entries beyond the new cap are immediately discarded.
  UNDOLIMIT: 100,
  // ── GRIPS family (GRIPS-SUBSYSTEM §5.11) ──────────────────────────
  // GRIPS — grip-glyph display master switch. Per Autodesk GRIPS docs:
  //   0 = grips OFF (no grips shown even on a selected object)
  //   1 = grips on objects (no block-component grips)
  //   2 = grips on objects AND on the components inside blocks
  // AutoCAD 2025 fresh-install default = 2.
  GRIPS: 2,
  // GRIPSIZE — grip-box size in pixels. AutoCAD range 1..255; default 5.
  GRIPSIZE: 5,
  // GRIPHOT — colour of a SELECTED (hot / warm) grip. ACI index 1..255;
  // AutoCAD default 12 (red).
  GRIPHOT: 12,
  // GRIPCOLOR — colour of an UNSELECTED (cold) grip. ACI index 1..255;
  // AutoCAD default 150 (blue).
  GRIPCOLOR: 150,
  // GRIPHOVER — colour of a grip when the cursor hovers over it. ACI
  // index 1..255; AutoCAD default 11.
  GRIPHOVER: 11,
} as const;

export type SysvarKey = keyof typeof DYN_DEFAULTS;
export type SysvarValues = Record<SysvarKey, number>;

/**
 * Validates a candidate integer value for the given sysvar against
 * AutoCAD's published numeric range. Returns false for NaN / non-integer
 * / out-of-range input; the store uses this gate to reject user-supplied
 * strings from SETVAR, DXF HEADER and localStorage alike.
 *
 * Note: AutoCAD silently accepts many legacy-clamped values; we are
 * stricter so that malformed persisted state never resurrects with a
 * weird value. DYN_DEFAULTS is always within range.
 */
export function isValidSysvarValue(key: SysvarKey, value: number): boolean {
  if (!Number.isFinite(value)) return false;
  // POLARANG / POLARDIST are the REAL-valued members — validate them BEFORE
  // the global integer gate below (a real increment like 22.5 is legal).
  if (key === "POLARANG") {
    // AutoCAD polar angle increment: strictly positive, ≤ 360°.
    return value > 0 && value <= 360;
  }
  if (key === "POLARDIST") {
    // AutoCAD polar-snap distance increment: non-negative (0 = fall back to
    // SNAP X-spacing, per the Autodesk POLARDIST reference); no documented
    // upper bound beyond "finite".
    return value >= 0;
  }
  if (!Number.isInteger(value)) return false;
  switch (key) {
    case "DYNMODE":
      return value >= -3 && value <= 3;
    case "DYNPICOORDS":
    case "DYNPIFORMAT":
    case "DYNPROMPT":
      // Binary Dynamic-Input toggles — 0/1 only. These MUST NOT share the
      // POLARMODE 0..15 case: folding them together silently widened all three
      // to accept 0..15, so `SETVAR DYNPICOORDS 15` (or a corrupt localStorage
      // entry) would persist a value the selectors only ever read as 0/1,
      // silently driving dynamic input into an unsupported state (#492 @codex P2).
      return value === 0 || value === 1;
    case "POLARMODE":
      // AutoCAD POLARMODE is a BITCODED sysvar (Autodesk POLARMODE reference):
      //   1  = measure polar angles relative to the last segment (else absolute)
      //   2  = object-snap tracking uses the polar tracking angles
      //        (OTRACK-SUBSYSTEM: `selectOtrackUsesPolarAngles`, folds the
      //        live POLAR angle table into OTRACK's alignment paths)
      //   4  = use the additional polar tracking angles (POLARADDANG)
      //   8  = press SHIFT to acquire object-snap tracking points
      //        (OTRACK-SUBSYSTEM: `selectOtrackShiftToAcquire`, gates
      //        hover-dwell acquisition on a held Shift key)
      // Autodesk-documented range 0..15. `SETVAR POLARMODE 4` (enable
      // POLARADDANG) MUST be accepted (#492 @codex P2 — the old 0/1 gate
      // rejected every bit but 0/1).
      // NB: this app treats POLARMODE bit 0 (POLARMODE_TRACKING_ON_BIT) as the
      // polar-tracking master "on" switch (see selectEffectivePolar) — a
      // pragmatic #492 deviation because AutoCAD itself has no documented F10
      // on/off GETVAR (the real bit-1 "relative to last segment" meaning is
      // NOT exposed as a getvar-able on/off either way). Bit 4 round-trips via
      // selectPolarAddAngEnabled.
      //
      // N6 (POLAR-RELATIVE-BASIS-WIRE) — because bit 1 above is already the
      // master on/off switch, the real "measure relative to last segment"
      // semantics live at APP-EXTENSION bit 16 instead
      // (`POLARMODE_RELATIVE_BASIS_BIT` / `selectPolarAngleBasis` in
      // `sysvarStore.ts`) — a bit VALUE AutoCAD's own POLARMODE bitcode never
      // assigns (the highest individual bit it documents is value 8, per the
      // list above — the "0..15" range two comments up is the SUM of those
      // four documented bits, not an individual bit value), so bit 16 does
      // not collide with any real AutoCAD semantics. The value RANGE this
      // validator accepts is extended 0..15 → 0..31 to admit it.
      return value >= 0 && value <= 31;
    case "TRACKPATH":
      // AutoCAD TRACKPATH (System Variable) — bitcode, documented 0..3:
      //   0 = full-screen polar path + full-screen OSNAP tracking path
      //   1 = full-screen polar path; OSNAP tracking path point-limited
      //   2 = full-screen OSNAP tracking path; NO polar tracking path
      //   3 = NO polar tracking path; OSNAP tracking path point-limited
      // Only bit 2 ("no polar path") is behaviourally wired in this app (see
      // the DYN_DEFAULTS.TRACKPATH comment) — the schema still validates the
      // full documented range so SETVAR/GETVAR round-trips every value.
      return value >= 0 && value <= 3;
    case "DYNTOOLTIPS":
    case "TEMPOVERRIDES":
    case "ORTHOMODE":
    case "OTRACKMODE":
    case "SNAPMODE":
    case "GRIDMODE":
    case "LWDISPLAY":
    case "RMBENTER":
    case "ROLLOVERTIPS":
    case "MIRRTEXT":
      return value === 0 || value === 1;
    case "DYNPIVIS":
    case "DYNDIVIS":
      return value >= 0 && value <= 2;
    case "DYNDIGRIP":
      return value >= 0 && value <= 31;
    case "OSMODE":
      // Object-snap bitmask. The 14 documented snap bits (1, 2, 4, …, 8192)
      // sum to 16383, PLUS the 16384 suppress-all bit (turns running snaps OFF
      // while preserving the saved settings — Autodesk OSMODE reference). The
      // full valid integer range is therefore 0..32767 (16383 + 16384). Real
      // files use the suppress bit (e.g. 20583 = 16384 + 4199); the previous
      // 16383 cap rejected those during hydration, leaving the stale live
      // default to corrupt the file on the first save (#342 F4).
      return value >= 0 && value <= 32767;
    case "AUTOSNAP":
      // AUTOSNAP is a 6-bit bitmask (0..63 documented). AutoCAD also
      // accepts a -1 sentinel via SETVAR to enable all bits, but our
      // schema layer rejects that — callers compose the bits explicitly.
      return value >= 0 && value <= 63;
    case "APERTURE":
      // APERTURE half-size in pixels. AutoCAD-documented range 1..50.
      return value >= 1 && value <= 50;
    case "PICKBOX":
      // PICKBOX half-size in pixels. AutoCAD-documented range 0..50.
      return value >= 0 && value <= 50;
    case "CURSORSIZE":
      // CURSORSIZE percentage of smaller viewport dimension. 1..100.
      return value >= 1 && value <= 100;
    case "CURSORCOLOR":
      // ACI palette index — 0..256 (0 = ByBlock, 256 = ByLayer; 7 = the
      // contrast / black-or-white auto-flip colour AutoCAD uses by
      // default). The full ACI range is 0..256 inclusive.
      return value >= 0 && value <= 256;
    case "SELECTIONPREVIEW":
      // 0..3 (bitmask of bits 1 and 2).
      return value >= 0 && value <= 3;
    case "PREVIEWEFFECT":
      // 0 = outline, 1 = glow+fill, 2 = both. AutoCAD-documented.
      return value === 0 || value === 1 || value === 2;
    case "PREVIEWFILTER":
      // 6-bit mask documented 0..63.
      return value >= 0 && value <= 63;
    case "SELECTIONCYCLING":
      // 0 = off, 1 = badge only, 2 = badge + listbox (AutoCAD-documented).
      return value === 0 || value === 1 || value === 2;
    case "UNDOLIMIT":
      // 0 = undo disabled, 1..10 000 = max past-stack depth (AutoCAD range).
      return value >= 0 && value <= 10000;
    case "GRIPS":
      // 0 = off, 1 = object grips, 2 = object + block grips.
      return value === 0 || value === 1 || value === 2;
    case "GRIPSIZE":
      // Grip-box pixel size. AutoCAD-documented range 1..255. Integer pixels —
      // Codex P3-8 (#317): a fractional size (1.5) is not a valid pixel count.
      return Number.isInteger(value) && value >= 1 && value <= 255;
    case "GRIPHOT":
    case "GRIPCOLOR":
    case "GRIPHOVER":
      // ACI colour index for selected / unselected / hover grips.
      // AutoCAD-documented range 1..255. Codex P3-8 (#317): an ACI index MUST
      // be an integer — a fractional value (1.5) cannot resolve through the ACI
      // map (SETVAR would report success but the grip falls back to default),
      // so reject non-integers here, not just out-of-range values.
      return Number.isInteger(value) && value >= 1 && value <= 255;
  }
}

/**
 * Type guard — narrows an arbitrary string to a known SysvarKey. Used
 * by SETVAR parser to validate user-supplied names before indexing into
 * DYN_DEFAULTS.
 */
export function isSysvarKey(name: string): name is SysvarKey {
  return Object.prototype.hasOwnProperty.call(DYN_DEFAULTS, name);
}
